import { useState, useEffect } from "react";
import axios from "axios";
import {
  Check, X, Loader2, Send, Lock, Sparkles, ChevronDown, ChevronUp,
  TrendingUp, TrendingDown, Minus, Target, Video, PoundSterling, Bell,
} from "lucide-react";
import { Button } from "../components/ui/button";
import VoiceReflectionRecorder from "../components/instructor/VoiceReflectionRecorder";
import QueueAIReviewButton from "../components/lesson/QueueAIReviewButton";
import { useTruthState, TRUTH_STATE_NEEDS_INPUT } from "../hooks/useTruthState";
import { TruthReceipt } from "../components/TruthReceipt";

const API = process.env.REACT_APP_BACKEND_URL;

// Phase B Addendum · Feb 2026 — Five doctrine prompts the instructor
// talks through in ONE voice recording. Guidance, not five separate
// forms. Kept module-scope so the pin scanner can lock the copy.
const VOICE_REFLECTION_PROMPTS = Object.freeze([
  "What was today's goal?",
  "What went well?",
  "What still needs work?",
  "What responsibility did the learner take?",
  "What should happen next lesson?",
]);

/**
 * EndLessonFlow — AUTOMATION FIRST.
 * One-tap debrief: instructor types → submit → system auto-scores & saves.
 * Optional "Adjust skills" panel and "Share summary with pupil" button on Done step.
 * No forced review-and-reply admin task.
 */
export default function EndLessonFlow({ lesson, onComplete, onCancel }) {
  const [step, setStep] = useState("offload"); // offload | processing | done
  const [rawNotes, setRawNotes] = useState("");
  const [reflectionId, setReflectionId] = useState("");
  const [translated, setTranslated] = useState("");
  const [signals, setSignals] = useState({ went_well: [], needs_work: [], confidence_delta: 0 });
  const [allowedSkills, setAllowedSkills] = useState([]);
  const [scoreboard, setScoreboard] = useState(null);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shared, setShared] = useState(false);
  const [reminding, setReminding] = useState(false);
  const [reminded, setReminded] = useState(false);
  // Batch P1 · Lesson Truth-State — single canonical receipt for
  // every EndLessonFlow async action. Bespoke `toast.success` /
  // `toast.error` calls have been removed; validation uses the
  // canonical `needs_input` state instead of a red toast.
  const truth = useTruthState();
  // ADI L3.2 continuity batch (Feb 28, 2026) — Break #5: surface the
  // instructor's OWN previous private notes for this pupil so they
  // don't have to mentally reconstruct prior thinking during the
  // debrief. Reuses the existing /api/lesson-events/history endpoint.
  // Silent when no previous notes exist. No analytics, no AI, no
  // interpretation — pure chronology of the instructor's own words.
  const [previousNotes, setPreviousNotes] = useState([]); // [{ date, notes }, ...]

  // Best-effort one-shot fetch of last private notes. Failures stay
  // silent — the offload step still works without recap.
  useEffect(() => {
    const pupilId = lesson?.work_student_id || lesson?.pupil_id;
    if (!pupilId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/api/lesson-events/history/${pupilId}`,
          { withCredentials: true },
        );
        if (cancelled) return;
        const items = Array.isArray(data?.lessons) ? data.lessons : [];
        // Most-recent-first chronology. Exclude the currently-active
        // lesson (we're debriefing it right now) and any rows
        // without genuine instructor notes.
        const recap = items
          .filter((ln) => ln && ln.id !== lesson.id)
          .filter((ln) => (ln.notes || "").trim().length > 0)
          .sort((a, b) => {
            const ad = `${a.date || ""}T${a.start_time || "00:00"}`;
            const bd = `${b.date || ""}T${b.start_time || "00:00"}`;
            return bd.localeCompare(ad);
          })
          .slice(0, 3)
          .map((ln) => ({
            date: ln.date || "",
            start_time: ln.start_time || "",
            notes: (ln.notes || "").trim(),
          }));
        setPreviousNotes(recap);
      } catch {
        // Silent — no fallback copy, no error toast.
      }
    })();
    return () => { cancelled = true; };
  }, [lesson?.id, lesson?.work_student_id, lesson?.pupil_id]);

  const submitOffload = async () => {
    if (!rawNotes.trim()) {
      truth.setState(TRUTH_STATE_NEEDS_INPUT, "Write a few words about the lesson.");
      return;
    }
    setStep("processing");
    try {
      await truth.run(
        async () => {
          const { data } = await axios.post(`${API}/api/reflections/instructor/offload`, {
            lesson_id: lesson.id,
            raw_notes: rawNotes,
            pupil_name: lesson.title || lesson.pupil_name || "the learner",
          }, { withCredentials: true });
          setReflectionId(data.reflection_id);
          setTranslated(data.translated_feedback || "");
          setSignals(data.skill_signals || { went_well: [], needs_work: [], confidence_delta: 0 });
          setAllowedSkills(data.allowed_skills || []);
          setScoreboard(data.scoreboard_update || null);
          setStep("done");
          return data;
        },
        {
          completedMessage: "Saved and scored.",
          failedMessage:    "Couldn't save the reflection.",
        },
      );
    } catch (err) {
      // truth.run captured the failure. Return the user to the
      // offload step so they can retry.
      setStep("offload");
    }
  };

  const toggleSkill = (skill, bucket) => {
    setSignals(s => {
      const went = [...(s.went_well || [])];
      const needs = [...(s.needs_work || [])];
      if (bucket === "went_well") {
        const idx = went.indexOf(skill);
        if (idx >= 0) went.splice(idx, 1);
        else { went.push(skill); const n = needs.indexOf(skill); if (n >= 0) needs.splice(n, 1); }
      } else {
        const idx = needs.indexOf(skill);
        if (idx >= 0) needs.splice(idx, 1);
        else { needs.push(skill); const w = went.indexOf(skill); if (w >= 0) went.splice(w, 1); }
      }
      return { ...s, went_well: went, needs_work: needs };
    });
  };

  const bumpConfidence = (dir) => {
    setSignals(s => ({
      ...s,
      confidence_delta: Math.max(-2, Math.min(2, (s.confidence_delta || 0) + dir)),
    }));
  };

  const saveAdjustments = async () => {
    setSaving(true);
    try {
      await truth.run(
        async () => {
          await axios.post(`${API}/api/reflections/instructor/adjust-skills`, {
            reflection_id: reflectionId,
            skill_updates: signals,
          }, { withCredentials: true });
          setAdjustOpen(false);
        },
        {
          completedMessage: "Scoreboard adjusted.",
          failedMessage:    "Couldn't save adjustments.",
        },
      );
    } catch { /* truth.run captured; TruthReceipt shows Retry */ }
    setSaving(false);
  };

  const shareWithPupil = async () => {
    setSharing(true);
    try {
      await truth.run(
        async () => {
          await axios.post(`${API}/api/reflections/instructor/share-with-pupil`, {
            reflection_id: reflectionId,
          }, { withCredentials: true });
          setShared(true);
        },
        {
          completedMessage: "Summary sent to pupil.",
          failedMessage:    "Couldn't send the summary.",
        },
      );
    } catch { /* truth.run captured; TruthReceipt shows Retry */ }
    setSharing(false);
  };

  const remindPupilToUpload = async () => {
    if (!lesson?.message_thread_id) {
      truth.setState(TRUTH_STATE_NEEDS_INPUT, "No message thread for this pupil yet.");
      return;
    }
    setReminding(true);
    const first = (lesson.pupil_name || lesson.title || "").split(" ")[0] || "there";
    const isPremium = !!lesson.passport_addon?.premium_review_booked;
    const body = isPremium
      ? `Hi ${first} — quick nudge: you booked a Premium Review (£7) for today's lesson. Upload your clip on your Passport page and the AI review will generate automatically. Takes ~20 seconds.`
      : `Hi ${first} — quick nudge: you booked a Progress Clip (£2) for today's lesson. Upload it on your Passport page when you can — saves to your learner-owned Passport and can be shared with any instructor.`;
    try {
      await truth.run(
        async () => {
          await axios.post(
            `${API}/api/threads/${lesson.message_thread_id}/messages`,
            { body, channel: "both" },
            { withCredentials: true },
          );
          setReminded(true);
        },
        {
          completedMessage: "Reminder sent.",
          failedMessage:    "Couldn't send the reminder.",
        },
      );
    } catch { /* truth.run captured; TruthReceipt shows Retry */ }
    setReminding(false);
  };

  // Batch P1 · Lesson Truth-State — calm auto-clear for terminal
  // successes. Failed / needs_input states linger until the user
  // dismisses or retries them (Truth Doctrine — never a dead-end).
  useEffect(() => {
    if (truth.state === "completed") {
      const t = setTimeout(() => truth.setState(null), 1200);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [truth]);

  return (
    <div data-testid="end-lesson-flow" className="p-5 space-y-4">
      <div className="w-10 h-1 bg-slate-200 dark:bg-slate-600 rounded-full mx-auto" />

      {/* Batch P1 · Lesson Truth-State — canonical async receipt.
          Docked above the flow steps so success / failure / needs-
          input always land in the same place. Failed states expose
          Retry + Dismiss (never a dead-end). */}
      <TruthReceipt
        state={truth.state}
        message={truth.message}
        onRetry={submitOffload}
        onDismiss={() => truth.setState(null)}
      />

      {/* ── STEP 1: End-of-lesson reflection · voice-first ──
          Phase B Addendum (Feb 2026): the default experience is a
          guided voice reflection. The instructor talks through five
          calm prompts in ONE recording; evidence attaches to the
          lesson via the R2 pipeline + evidence.voice_reflection
          finalize endpoint, and the lesson_reflection_missing
          pending action resolves automatically.

          The pre-existing written debrief is preserved (some
          instructors prefer to type, some need to type) — it now
          lives inside the collapsed "Prefer to type instead?"
          disclosure below. Either path satisfies the reflection
          requirement; the offload endpoint idempotently resolves
          the same pending action. */}
      {step === "offload" && (
        <div data-testid="offload-step" className="space-y-4">
          <div className="text-center">
            <h2 className="text-xl font-extrabold text-slate-900 dark:text-white">
              End-of-lesson reflection
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Talk through what happened. We&rsquo;ll guide you through the key points.
            </p>
          </div>

          <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-xl p-3 flex items-start gap-2">
            <Lock className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700 dark:text-amber-400">
              Your reflection is <strong>private</strong>. We use it to auto-update the pupil&rsquo;s skill circles and memory &mdash; nothing is sent to the pupil automatically.
            </p>
          </div>

          {/* ADI L3.2 — Break #5: calm collapsed recap of the
              instructor's OWN previous private notes for this pupil.
              Continuity of thought, not note-management software.
              Silent when no previous notes exist (no fallback copy). */}
          {previousNotes.length > 0 && (
            <details
              data-testid="previous-notes-disclosure"
              className="bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded-xl"
            >
              <summary
                data-testid="previous-notes-summary"
                className="cursor-pointer select-none px-4 py-3 text-sm font-bold text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white"
              >
                Recap of your last private notes &rarr;
              </summary>
              <div
                data-testid="previous-notes-body"
                className="px-4 pb-4 pt-1 space-y-3"
              >
                {previousNotes.map((entry, idx) => (
                  <div
                    key={`${entry.date}-${idx}`}
                    data-testid={`previous-notes-entry-${idx}`}
                    className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed"
                  >
                    <p className="font-bold text-slate-500 dark:text-slate-400 mb-1">
                      {entry.date || "Earlier"}
                      {entry.start_time ? ` · ${entry.start_time}` : ""}
                    </p>
                    <p className="whitespace-pre-wrap">{entry.notes}</p>
                  </div>
                ))}
              </div>
            </details>
          )}

          {/* Voice-first recorder with the five doctrine prompts.
              One recording covers all five — the prompts are
              guidance, not five separate forms. */}
          <VoiceReflectionRecorder
            lessonId={lesson?.id}
            prompts={VOICE_REFLECTION_PROMPTS}
            onDone={() => setStep("done")}
          />

          {/* Optional fallback: existing written question form,
              collapsed by default. Instructor can prefer typing;
              the offload endpoint satisfies the same lesson
              reflection lifecycle as the voice path. */}
          <details
            data-testid="written-reflection-disclosure"
            className="bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700 rounded-xl"
          >
            <summary
              data-testid="written-reflection-summary"
              className="cursor-pointer select-none px-4 py-3 text-sm font-semibold text-slate-700 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white"
            >
              Prefer to type instead?
            </summary>
            <div
              data-testid="written-reflection-body"
              className="px-4 pb-4 pt-1 space-y-3"
            >
              <textarea data-testid="raw-notes-input"
                value={rawNotes} onChange={e => setRawNotes(e.target.value)}
                placeholder="e.g. struggled with roundabouts, confidence dipped, mirrors improving, needs clutch work next time..."
                className="w-full h-32 border border-slate-200 dark:border-slate-600 rounded-2xl px-4 py-3 text-base bg-white dark:bg-slate-700 resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <Button data-testid="submit-offload" onClick={submitOffload}
                disabled={!rawNotes.trim()}
                className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold">
                Save &amp; Update Scoreboard
              </Button>
            </div>
          </details>

          <button data-testid="cancel-offload" onClick={onCancel}
            className="w-full text-center text-sm text-slate-400 hover:text-slate-600 font-medium py-1">
            Skip for now
          </button>
        </div>
      )}

      {/* ── STEP 2: Processing ── */}
      {step === "processing" && (
        <div data-testid="processing-step" className="text-center py-10 space-y-3">
          <Loader2 className="w-10 h-10 text-blue-500 animate-spin mx-auto" />
          <p className="text-base font-bold text-slate-900 dark:text-white">Scoring lesson...</p>
          <p className="text-sm text-slate-400">Updating skill circles & preparing next-lesson memory</p>
        </div>
      )}

      {/* ── STEP 3: Done ── */}
      {step === "done" && (
        <div data-testid="done-step" className="space-y-4">
          <div className="text-center space-y-2">
            <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/20 flex items-center justify-center mx-auto">
              <Check className="w-8 h-8 text-emerald-600" />
            </div>
            <p className="text-lg font-extrabold text-slate-900 dark:text-white">Saved & Scored</p>
            <p className="text-sm text-slate-400">Skill circles updated. Memory ready for next lesson.</p>
          </div>

          {/* Scoreboard summary */}
          {scoreboard && scoreboard.resolved && (
            <div data-testid="scoreboard-summary" className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl p-4 space-y-2 text-xs">
              {signals.went_well?.length > 0 && (
                <div className="flex items-start gap-2">
                  <TrendingUp className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <p className="text-slate-600 dark:text-slate-300">
                    <span className="font-bold text-emerald-600">Improving:</span> {signals.went_well.join(", ")}
                  </p>
                </div>
              )}
              {signals.needs_work?.length > 0 && (
                <div className="flex items-start gap-2">
                  <Target className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                  <p className="text-slate-600 dark:text-slate-300">
                    <span className="font-bold text-amber-600">Needs work:</span> {signals.needs_work.join(", ")}
                  </p>
                </div>
              )}
              {scoreboard.previous_confidence != null && scoreboard.new_confidence != null && (
                <div className="flex items-start gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-violet-500 mt-0.5 flex-shrink-0" />
                  <p className="text-slate-600 dark:text-slate-300">
                    <span className="font-bold text-violet-600">Confidence:</span> {scoreboard.previous_confidence} → {scoreboard.new_confidence}
                  </p>
                </div>
              )}
            </div>
          )}

          {!scoreboard?.resolved && (
            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3 text-xs text-slate-500">
              Debrief saved. Scoreboard update skipped — this pupil isn't linked to a Work Mode profile yet.
            </div>
          )}

          {/* Passport add-on alert — only shows if pupil pre-booked a paid clip / premium review for this lesson */}
          {lesson?.passport_addon?.has_addon && (
            <div
              data-testid="passport-addon-alert"
              className="rounded-2xl border border-indigo-200 dark:border-indigo-900/40 bg-indigo-50 dark:bg-indigo-900/15 p-4"
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-indigo-600 flex items-center justify-center flex-shrink-0">
                  {lesson.passport_addon.premium_review_booked
                    ? <Sparkles className="w-4 h-4 text-white" />
                    : <Video className="w-4 h-4 text-white" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-extrabold text-indigo-900 dark:text-indigo-200">
                    {lesson.passport_addon.premium_review_booked
                      ? "Pupil booked Premium Review (£7)"
                      : "Pupil booked Progress Clip (£2)"}
                  </p>
                  <p className="text-[11px] text-indigo-700/80 dark:text-indigo-300/80 mt-1 leading-relaxed">
                    Remind them to upload from their Passport page. Their clip gets auto-saved
                    and {lesson.passport_addon.premium_review_booked ? "AI generates a review" : "added to their Passport"}.
                  </p>
                  {(lesson.passport_addon.instructor_earns_pence || 0) > 0 && (
                    <p
                      data-testid="passport-addon-earnings"
                      className="inline-flex items-center gap-1 mt-2 text-[11px] font-bold px-2 py-1 rounded-full bg-white dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800"
                    >
                      <PoundSterling className="w-3 h-3" />
                      You earn £{(lesson.passport_addon.instructor_earns_pence / 100).toFixed(2)} once they upload
                    </p>
                  )}
                  {lesson.message_thread_id && (
                    reminded ? (
                      <p
                        data-testid="passport-addon-reminded"
                        className="mt-3 text-[11px] font-bold text-emerald-700 dark:text-emerald-300 flex items-center gap-1"
                      >
                        <Check className="w-3 h-3" /> Reminder sent to pupil
                      </p>
                    ) : (
                      <Button
                        data-testid="passport-addon-remind-btn"
                        onClick={remindPupilToUpload}
                        disabled={reminding}
                        size="sm"
                        className="mt-3 w-full h-9 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold"
                      >
                        {reminding ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <>
                            <Bell className="w-3.5 h-3.5 mr-1.5" />
                            Remind pupil to upload
                          </>
                        )}
                      </Button>
                    )
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Adjust skills (collapsible, optional) */}
          <div className="border-t border-slate-200 dark:border-slate-700 pt-3">
            <button data-testid="toggle-adjust" onClick={() => setAdjustOpen(v => !v)}
              className="w-full flex items-center justify-between text-xs font-bold text-slate-500 hover:text-slate-700 dark:text-slate-400 uppercase tracking-wider">
              <span>Adjust if AI got something wrong</span>
              {adjustOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>

          {adjustOpen && (
            <div data-testid="adjust-panel" className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4 space-y-3">
              <div>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <TrendingUp className="w-3.5 h-3.5 text-emerald-500" />
                  <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider">Improving</p>
                </div>
                <div className="flex flex-wrap gap-1.5" data-testid="went-well-chips">
                  {allowedSkills.map(s => {
                    const on = signals.went_well?.includes(s);
                    return (
                      <button key={`w-${s}`} data-testid={`chip-well-${s.replace(/\s+/g, "-").toLowerCase()}`}
                        onClick={() => toggleSkill(s, "went_well")}
                        className={`px-2 py-0.5 rounded-full text-[10px] font-bold border transition-all ${
                          on ? "bg-emerald-500 text-white border-emerald-500"
                             : "bg-white dark:bg-slate-700 text-slate-500 border-slate-200 dark:border-slate-600"
                        }`}>{s}</button>
                    );
                  })}
                </div>
              </div>
              <div>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Target className="w-3.5 h-3.5 text-amber-500" />
                  <p className="text-[10px] font-bold text-amber-600 uppercase tracking-wider">Needs Work</p>
                </div>
                <div className="flex flex-wrap gap-1.5" data-testid="needs-work-chips">
                  {allowedSkills.map(s => {
                    const on = signals.needs_work?.includes(s);
                    return (
                      <button key={`n-${s}`} data-testid={`chip-work-${s.replace(/\s+/g, "-").toLowerCase()}`}
                        onClick={() => toggleSkill(s, "needs_work")}
                        className={`px-2 py-0.5 rounded-full text-[10px] font-bold border transition-all ${
                          on ? "bg-amber-500 text-white border-amber-500"
                             : "bg-white dark:bg-slate-700 text-slate-500 border-slate-200 dark:border-slate-600"
                        }`}>{s}</button>
                    );
                  })}
                </div>
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Confidence</p>
                <div className="flex items-center gap-2" data-testid="confidence-delta">
                  <button data-testid="conf-down" onClick={() => bumpConfidence(-1)}
                    className="w-9 h-9 rounded-xl border border-slate-200 dark:border-slate-600 text-red-500 active:scale-95 flex items-center justify-center">
                    <TrendingDown className="w-4 h-4" />
                  </button>
                  <div className="flex-1 text-center">
                    <p data-testid="conf-delta-value" className={`text-base font-extrabold ${
                      signals.confidence_delta > 0 ? "text-emerald-600" : signals.confidence_delta < 0 ? "text-red-500" : "text-slate-400"
                    }`}>{signals.confidence_delta > 0 ? `+${signals.confidence_delta}` : signals.confidence_delta}</p>
                  </div>
                  <button data-testid="conf-flat" onClick={() => setSignals(s => ({ ...s, confidence_delta: 0 }))}
                    className="w-9 h-9 rounded-xl border border-slate-200 dark:border-slate-600 text-slate-400 active:scale-95 flex items-center justify-center">
                    <Minus className="w-4 h-4" />
                  </button>
                  <button data-testid="conf-up" onClick={() => bumpConfidence(1)}
                    className="w-9 h-9 rounded-xl border border-slate-200 dark:border-slate-600 text-emerald-500 active:scale-95 flex items-center justify-center">
                    <TrendingUp className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <Button data-testid="save-adjust" onClick={saveAdjustments} disabled={saving}
                className="w-full h-10 bg-slate-700 hover:bg-slate-800 text-white rounded-xl text-sm font-bold">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Adjustments"}
              </Button>
            </div>
          )}

          {/* Optional: share summary with pupil */}
          {translated && !shared && (
            <div className="border-t border-slate-200 dark:border-slate-700 pt-3 space-y-2">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Optional</p>
              <div className="bg-blue-50 dark:bg-blue-900/10 rounded-xl p-3">
                <p className="text-xs text-slate-600 dark:text-slate-400 italic leading-relaxed">"{translated}"</p>
              </div>
              <Button data-testid="share-with-pupil" onClick={shareWithPupil} disabled={sharing}
                className="w-full h-10 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-bold">
                {sharing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Share summary with pupil
              </Button>
            </div>
          )}
          {shared && (
            <p className="text-xs text-emerald-600 text-center font-bold">✓ Summary sent to pupil</p>
          )}

          {/* Queue for AI review later (Feb 8, 2026) — surfaces the
              deferred-upload path so the instructor can finish the
              debrief now and upload the lesson video from a laptop
              tonight. Cheap, free, and reuses the existing R2 +
              processing pipeline once they upload. */}
          <QueueAIReviewButton lesson={lesson} />

          {/* Continuity CTA · Founder Op Experience V1 · Feb 2026
              Bridges "lesson finished" → "prep tomorrow's lesson"
              so the instructor sees ONE connected system rather
              than being dropped back onto the dashboard. Closes the
              gap flagged in iteration 225 friction report. */}
          <a
            data-testid="prep-next-lesson-cta"
            href={(() => {
              const sid = lesson?.work_student_id || lesson?.pupil_id;
              return sid ? `/work-mode?arrive=${encodeURIComponent(sid)}` : "/work-mode";
            })()}
            onClick={() => onComplete?.()}
            className="w-full h-11 bg-indigo-600 hover:bg-indigo-500
                       text-white rounded-xl font-bold mt-2
                       flex items-center justify-center gap-2
                       transition-colors"
          >
            Prep tomorrow&apos;s lesson
            <span aria-hidden="true">→</span>
          </a>

          <Button data-testid="finish-flow" onClick={() => onComplete?.()}
            className="w-full h-11 bg-slate-800/60 hover:bg-slate-800 text-slate-300 rounded-xl font-semibold mt-1 text-sm">
            Done for today
          </Button>
        </div>
      )}
    </div>
  );
}
